/**
 * Theme applier — the single, prescribed way to switch themes.
 *
 * A theme is a set of palette-variable overrides keyed by a `data-theme`
 * attribute on <html> (see variables.css: `:root[data-theme='dark']`).
 * Switching themes = changing that attribute; every component that consumes
 * the palette variables re-themes automatically.
 *
 * The same mechanism powers built-in dark mode today and, later, remote
 * white-label themes (inject `:root[data-theme='acme'] { ... }` and call
 * `setTheme('acme')`).
 *
 * This is plain JS by design — no React — so it can run at bootstrap before
 * the app renders. Consume it from @xemplo/app-bootstrap alongside the font
 * loader.
 */

/** Built-in themes. `string` widening allows future white-label theme names. */
export type ThemeName = 'light' | 'dark' | (string & {});

const STORAGE_KEY = 'xemplo-theme';

/**
 * Safe theme-name shape: lowercase letters, digits and hyphens. Covers the
 * built-ins (`light`, `dark`) and future white-label names (`acme`) while
 * rejecting malformed or garbage values before they are stamped onto the
 * `data-theme` DOM attribute.
 */
const SAFE_THEME_PATTERN = /^[a-z0-9-]+$/;

/** Stamp a theme onto <html> via the `data-theme` attribute. */
export function setTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

/** The OS-level colour-scheme preference, defaulting to light. */
export function getPreferredColorScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * A previously persisted theme choice, or null if the user never chose one (or
 * the stored value is malformed). Validated against `SAFE_THEME_PATTERN` so a
 * corrupted/injected storage value can't be stamped onto the DOM attribute.
 */
export function getStoredTheme(): ThemeName | null {
  if (typeof localStorage === 'undefined') return null;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === null || !SAFE_THEME_PATTERN.test(stored)) return null;
  return stored;
}

/** Persist a theme choice and apply it immediately. Use for a user-facing toggle. */
export function selectTheme(theme: ThemeName): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, theme);
  setTheme(theme);
}

/**
 * Resolve the initial theme (stored choice, else OS preference) and apply it.
 * Call once during app bootstrap. Returns the theme that was applied.
 */
export function initTheme(enforcedTheme?: ThemeName): ThemeName {
  const theme = enforcedTheme ?? getStoredTheme() ?? getPreferredColorScheme();
  setTheme(theme);
  return theme;
}
